import type { SearchResponse } from 'typesense/lib/Typesense/Documents';
import type { SetlistContext } from './client';
import {
  MOTION_GRAPHICS_COLLECTION,
  MUSIC_COLLECTION,
  SFX_COLLECTION,
  STOCK_VIDEO_COLLECTION,
} from './collections';
import { EXCLUDE_LUTS_FILTER, LUTS_FILTER } from './lut-filters';
import {
  composeStockVideoSort,
  DEFAULT_VIDEO_ENGAGEMENT_SLUG,
  STOCK_VIDEO_INCLUDE_FIELDS,
  VIDEO_ENGAGEMENT_SORT,
  VIDEO_RECENCY_TIE_BREAKER,
  withEngagementFilter,
} from './stock-video-engagement-join';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

// Synthetic collection identifier that stands in for MOTION_GRAPHICS_COLLECTION
// with `is_lut:true` applied. Kept as a distinct string so the search page
// can branch on it without another out-of-band flag.
export const LUTS_SEARCH_COLLECTION = 'motiongraphics.v2:luts';

export const queryByMap: Record<string, string> = {
  [MUSIC_COLLECTION]: 'name, keywords, contributor_name, mood, genre, theme',
  [SFX_COLLECTION]: 'name, contributor_name, keywords',
  [MOTION_GRAPHICS_COLLECTION]: 'name, keywords',
  [STOCK_VIDEO_COLLECTION]: 'title, keywords, embedding',
  [LUTS_SEARCH_COLLECTION]: 'name, keywords',
};

export const fetchSearchResults = async (
  context: SetlistContext,
  {
    searchTerm,
    pageNumber,
    type,
    filter_by,
    sort_by,
    isSubscribedUser = false,
    slug = DEFAULT_VIDEO_ENGAGEMENT_SLUG,
    perPage = 20,
  }: {
    pageNumber: number;
    searchTerm: string;
    type:
      | typeof MUSIC_COLLECTION
      | typeof SFX_COLLECTION
      | typeof MOTION_GRAPHICS_COLLECTION
      | typeof STOCK_VIDEO_COLLECTION
      | typeof LUTS_SEARCH_COLLECTION;
    filter_by?: string;
    sort_by?: string;
    limit?: number;
    isSubscribedUser?: boolean;
    slug?: string;
    perPage?: number;
  }
): Promise<SearchResponse<object> | null> => {
  if (!searchTerm) {
    return null;
  }

  const isStockVideo = type === STOCK_VIDEO_COLLECTION;
  const isLuts = type === LUTS_SEARCH_COLLECTION;
  const isMotionGraphics = type === MOTION_GRAPHICS_COLLECTION;

  // LUTs are searched against the motion graphics collection with the
  // is_lut:true filter merged into the caller's filter_by.
  const collectionForSearch = isLuts ? MOTION_GRAPHICS_COLLECTION : type;
  let effectiveFilterBy = filter_by;
  if (isLuts) {
    effectiveFilterBy = filter_by
      ? `${filter_by} && ${LUTS_FILTER}`
      : LUTS_FILTER;
  }
  // motiongraphics.v2 also holds LUTs (is_lut:true); scope the MG search to the
  // MG side so LUTs don't leak into motion graphics results.
  if (isMotionGraphics) {
    effectiveFilterBy = filter_by
      ? `${filter_by} && ${EXCLUDE_LUTS_FILTER}`
      : EXCLUDE_LUTS_FILTER;
  }
  // For stock video, merge the left join filter into filter_by
  if (isStockVideo) {
    const accountTypeGroup = isSubscribedUser ? 'paid' : 'free';
    effectiveFilterBy = withEngagementFilter(accountTypeGroup, filter_by, slug);
  }

  const effectiveSortBy =
    sort_by ??
    (isStockVideo
      ? composeStockVideoSort([
          VIDEO_ENGAGEMENT_SORT,
          '_text_match:desc',
          VIDEO_RECENCY_TIE_BREAKER,
        ])
      : undefined);

  const searchParameters = {
    q: searchTerm,
    query_by: queryByMap[type],
    page: pageNumber,
    per_page: perPage,
    exhaustive_search: false,
    filter_by: effectiveFilterBy,
    ...(effectiveSortBy ? { sort_by: effectiveSortBy } : {}),
    ...(isStockVideo
      ? {
          vector_query: `embedding:([], distance_threshold:${VECTOR_SEARCH_DEFAULTS.SEARCH.DISTANCE_THRESHOLD})`,
          num_typos: 0,
          drop_tokens_threshold: 0,
          prefix: false,
          include_fields: STOCK_VIDEO_INCLUDE_FIELDS,
        }
      : {}),
  };

  const search = await context.client
    .collections(collectionForSearch)
    .documents()
    .search(searchParameters, {});

  return search;
};
